import QueryString from 'query-string';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';
import DOCUMENTS_CONTROLLER from '../../controllers/uploadedDocuments';
import GLOBAL_FILTER_CONTROLLER from '../../controllers/globalFilter';
import ComponentUtil from '../../utils/componentUtil';
import Logger, { ALL_MESSAGES, LOG_LEVELS } from '../../utils/logger';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import ApplicationMessages from '../inAppMessages/ApplicationMessages';
import { toastActions } from '../inAppMessages/toastActions';
import FilterPanel from './documentTable/FilterPanel';
import DocumentListForm from './documentTable/DocumentListForm';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';
import { COMPANY_CODE_FIELD, COMPANY_ID_FIELD, isAllCompanies, resolveCompanySelection } from '../../utils/companyFilterUtil';
import { getDefaultDateRange, isInvalidDateRange, normalizeStatusFilter } from '../../utils/uploadedDocumentsHelper';

const PAGE_INTERNAL_IDS = [COMPANY_ID_FIELD];
const QUERY_STRING_OPTIONS = { arrayFormat: 'index' };
const PERIOD_KEYS_TO_CLEAR = ['month', 'year', 'date'];
const FILTER_PANEL_DISMISS_BLOCK_MS = 500;

const log = new Logger('UPLOADED_DOCUMENTS_PAGE', LOG_LEVELS.DEBUG);

class UploadedDocumentsPage extends Component {
    constructor(props) {
        super(props);
        this.state = { filterPanelOpen: true, shouldRestoreFilterPanelOpen: false };
        this.filterPanelDismissBlockedUntil = 0;
        this.documentListFormRef = React.createRef();
        this.onSearchClickHandler = this.onSearchClickHandler.bind(this);
        this.onFieldChangeHandler = this.onFieldChangeHandler.bind(this);
        this.handleToggleFilterPanel = this.handleToggleFilterPanel.bind(this);
        this.handleFilterPanelDismiss = this.handleFilterPanelDismiss.bind(this);
    }

    doRequestedOperation(action, documentIds) {
        log.debug('doRequestedOperation(): ', action, documentIds);
        log.clearAllDictionaryErrors();
        if (_.isEmpty(documentIds))
            return;
        let values = this.prepareRequestForFileOperation(action, documentIds);

        this.props.doOperationOnDocument(values, (errors, successes) => {
            log.debug("doOperationOnDocument - this happened", action, errors, successes);

            if (errors) {
                const errorLanguageStringKey = action === 'delete' ? 'inAppMessage_App_Error_170' : 'inAppMessage_App_Error_182';
                this.props.doAddToast(
                    `uploaded-documents-${action}-operation-error`,
                    LANGUAGES_CONTROLLER.format(this.props.languages, errorLanguageStringKey),
                    '',
                    'error'
                );
            } else if (successes) {
                const languageStringKey = action === 'delete' ? 'inAppMessage_Success_40' : 'inAppMessage_Success_41';
                this.props.doAddToast(
                    `uploaded-documents-${action}-started`,
                    LANGUAGES_CONTROLLER.format(this.props.languages, languageStringKey),
                    '',
                    'success'
                );
            }
        });
    }

    prepareRequestForFileOperation(action, documentIds) {
        return {
            documentIds: documentIds,
            action: action,
            userId: this.props.userName,
            customerId: this.props.customerId
        };
    }

    doLoadDocumentDetailsHandler(documentId) {
        log.debug('doLoadDocumentDetailsHandler(): ', documentId);
        this.props.doLoadDocumentDetails(documentId);
    }

    doUnLoadDocumentDetailsHandler() {
        log.debug('doUnLoadDocumentDetailsHandler: ');
        this.filterPanelDismissBlockedUntil = Date.now() + FILTER_PANEL_DISMISS_BLOCK_MS;
        this.props.doUnLoadDocumentDetails();
    }

    /**
     * The page's effective filter criteria: the stored global filter, overridden by the URL, with
     * the current month filled in when no range is present at all. The URL always wins.
     */
    buildCriteria() {
        const stored = Object.assign({}, GLOBAL_FILTER_CONTROLLER.getAll() || {});
        delete stored.startDate;
        delete stored.endDate;

        const criteria = Object.assign({}, stored, QueryString.parse(this.props.location.search, QUERY_STRING_OPTIONS) || {});

        const defaults = getDefaultDateRange();
        criteria.startDate = criteria.startDate || defaults.startDate;
        criteria.endDate = criteria.endDate || defaults.endDate;
        criteria.status = normalizeStatusFilter(criteria.status);

        return Object.assign(criteria, resolveCompanySelection(criteria, this.props.companyList));
    }

    /**
     * Merges expected filter keys to the Global State to allow other pages to access the same.
     * Filename, manualDocumentsOnly and status are deliberately page-local and live in the URL only.
     */
    persistGlobalFilter(criteria = {}) {
        const persisted = { startDate: criteria.startDate, endDate: criteria.endDate };
        const removeKeys = [...PERIOD_KEYS_TO_CLEAR];

        if (isAllCompanies(criteria.companyId)) {
            removeKeys.push(COMPANY_ID_FIELD, COMPANY_CODE_FIELD);
        } else {
            persisted.companyId = criteria.companyId;
            persisted.companyCode = criteria.companyCode;
        }

        GLOBAL_FILTER_CONTROLLER.merge(persisted, removeKeys);
    }

    dispatchLoad(criteria = {}) {
        this.props.onPageLoad(this.toRequestParams(criteria));
    }

    updateUrl(criteria = {}) {
        const urlParams = this.toRequestParams(criteria);
        PAGE_INTERNAL_IDS.forEach((key) => delete urlParams[key]);
        if (`${urlParams.manualDocumentsOnly}` !== 'true') {
            delete urlParams.manualDocumentsOnly;
        }
        ComponentUtil.updateUrlParams(this.props.location, this.props.history, urlParams);
    }

    toRequestParams(criteria = {}) {
        return Object.assign({}, criteria, { status: [...normalizeStatusFilter(criteria.status)] });
    }

    onSearchClickHandler(selection) {
        log.debug('onSearchClickHandler() - selection: ', selection);
        log.clearAllDictionaryErrors();

        const criteria = Object.assign({}, selection, {
            status: normalizeStatusFilter(selection.status)
        }, resolveCompanySelection(selection, this.props.companyList));

        let invalid = false;
        if (isInvalidDateRange(criteria.startDate, criteria.endDate)) {
            log.setDictionaryMessage(ALL_MESSAGES.UPLOADED_DOCUMENTS_INVALID_DATE_RANGE_ERROR);
            invalid = true;
        }
        if (`${criteria.manualDocumentsOnly}` === 'true' && isAllCompanies(criteria.companyId)) {
            log.setDictionaryMessage(ALL_MESSAGES.MANUAL_DOCS_REQUIRES_COMPANY_ERROR);
            invalid = true;
        }
        if (invalid) {
            return;
        }

        this.updateCompanyFilterMessage(criteria.companyId);
        this.persistGlobalFilter(criteria);
        this.dispatchLoad(criteria);
        this.updateUrl(criteria);
        this.documentListFormRef.current?.getWrappedInstance()?.clearAllSelections();
    }

    onFieldChangeHandler(keyValue) {
        if (COMPANY_ID_FIELD in keyValue) {
            log.clearDictionaryError(ALL_MESSAGES.MANUAL_DOCS_REQUIRES_COMPANY_ERROR);
        }
        if ('startDate' in keyValue || 'endDate' in keyValue) {
            log.clearDictionaryError(ALL_MESSAGES.UPLOADED_DOCUMENTS_INVALID_DATE_RANGE_ERROR);
        }
    }

    updateCompanyFilterMessage(companyId) {
        if (isAllCompanies(companyId)) {
            log.clearDictionaryError(ALL_MESSAGES.UPLOADED_DOCUMENTS_COMPANY_FILTER_PROCESSED_ONLY);
        } else {
            log.setDictionaryMessage(ALL_MESSAGES.UPLOADED_DOCUMENTS_COMPANY_FILTER_PROCESSED_ONLY);
        }
    }

    handleToggleFilterPanel() {
        this.setState((prevState) => ({ filterPanelOpen: !prevState.filterPanelOpen }));
    }

    isDocumentOverlayOpen() {
        return Boolean(document.querySelector('s-dialog[open]:not([open="false"]), .uploaded-documents-overlay'));
    }

    handleFilterPanelDismiss() {
        if (this.isDocumentOverlayOpen() || Date.now() < this.filterPanelDismissBlockedUntil) {
            this.setState((prevState) => ({
                filterPanelOpen: true,
                shouldRestoreFilterPanelOpen: !prevState.shouldRestoreFilterPanelOpen
            }));
            return;
        }

        this.setState({ filterPanelOpen: false });
    }

    componentDidMount() {
        log.debug('componentDidMount()');
        log.clearAllDictionaryErrors();

        const criteria = this.buildCriteria();

        if (isInvalidDateRange(criteria.startDate, criteria.endDate)) {
            log.setDictionaryMessage(ALL_MESSAGES.UPLOADED_DOCUMENTS_INVALID_DATE_RANGE_ERROR);
        } else if ((criteria.companyCode !== undefined && criteria.companyId !== undefined)
            || (criteria.companyCode === undefined && criteria.companyId === undefined)) {
            this.persistGlobalFilter(criteria);
            this.dispatchLoad(criteria);
        }

        this.updateUrl(criteria);
        this.updateCompanyFilterMessage(criteria.companyId);
    }

    componentDidUpdate(prevProps) {
        if (this.props.companyList === prevProps.companyList || _.isEmpty(this.props.companyList)) {
            return;
        }

        const criteria = this.buildCriteria();
        if (isInvalidDateRange(criteria.startDate, criteria.endDate)) {
            return;
        }

        this.persistGlobalFilter(criteria);
        this.dispatchLoad(criteria);
        this.updateUrl(criteria);
    }

    componentWillUnmount() {
        log.clearAllDictionaryErrors();
    }

    render() {
        log.debug('render(): ', this.props);
        const criteria = this.buildCriteria();
        const appliedFilters = {
            companyId: criteria.companyId,
            startDate: criteria.startDate,
            endDate: criteria.endDate,
            filename: criteria.filename,
            manualDocumentsOnly: criteria.manualDocumentsOnly,
            status: criteria.status
        };

        const documentList = [].concat(this.props.uploadedDocuments || []).concat(this.props.processedDocuments || []);
        let filteredList = documentList;

        if (!_.isEmpty(criteria.filename)) {
            const lowerCaseFileName = criteria.filename.toLowerCase();
            filteredList = filteredList.filter((document) => {
                return document.fileName.toLowerCase().includes(lowerCaseFileName);
            });
        }

        return (
            <s-container data-test="component-uploaded-documents">
                <ReadOnlyWrapper>
                    <div className="content-area">
                        <h1><LanguageStringResolver string='uploadedDocumentsPage_UploadedDocuments' /></h1>
                        <p><LanguageStringResolver string='uploadedDocumentsPage_SeeAllOfTheFilesUploadedToTheSystem' /></p>
                        <ApplicationMessages
                            messageIds={[
                                ALL_MESSAGES.MANUAL_DOCS_REQUIRES_COMPANY_ERROR,
                                ALL_MESSAGES.UPLOADED_DOCUMENTS_INVALID_DATE_RANGE_ERROR
                            ]}
                            messageType="applicationError" />
                        <ApplicationMessages
                            messageIds={[ALL_MESSAGES.UPLOADED_DOCUMENTS_COMPANY_FILTER_PROCESSED_ONLY]}
                            messageType="applicationInfo"
                            disableDismiss />
                        <div className="uploaded-documents-table-section">
                            <s-row>
                                <s-col span="6">
                                    <button
                                        className={this.state.filterPanelOpen ? "toggle-button icon-leading mt10" : "secondary icon-leading mt10"}
                                        data-testid="uploaded-documents-filter-button"
                                        excludeFromReadonly={true}
                                        onClick={this.handleToggleFilterPanel}
                                    >
                                        <s-icon name="filter" />
                                        <LanguageStringResolver string="FilterButton" />
                                    </button>
                                </s-col>
                            </s-row>
                            <div className="uploaded-documents-filter-panel-wrapper">
                                <FilterPanel
                                    open={this.state.filterPanelOpen}
                                    shouldRestoreOpen={this.state.shouldRestoreFilterPanelOpen}
                                    appliedFilters={appliedFilters}
                                    onApply={this.onSearchClickHandler}
                                    onDismiss={this.handleFilterPanelDismiss}
                                    onFieldChange={this.onFieldChangeHandler}
                                />
                            </div>
                            <div className="uploaded-documents-list">
                                <DocumentListForm
                                    ref={this.documentListFormRef}
                                    documentList={filteredList}
                                    allSelected={true}
                                    onRequestAction={this.doRequestedOperation.bind(this)}
                                    loadDocumentDetails={this.doLoadDocumentDetailsHandler.bind(this)}
                                    documentDetails={this.props.documentDetails}
                                    documentDetailsError={this.props.documentDetailsError}
                                    UnloadDocumentDetails={this.doUnLoadDocumentDetailsHandler.bind(this)}
                                />
                            </div>
                            <div className="clear"></div>
                        </div>
                    </div>
                </ReadOnlyWrapper>
            </s-container>
        );
    }
}


function mapStateToProps(state) {
    log.debug('mapStateToProps(): ', state);
    return {
        languages: state.languages,
        companyList: (state.companies && state.companies.data) || [],
        processedDocuments: state.uploadedDocuments && state.uploadedDocuments.processedDocuments,
        uploadedDocuments: state.uploadedDocuments && state.uploadedDocuments.uploadedFiles,
        documentDetails: state.uploadedDocuments && state.uploadedDocuments.documentDetails,
        documentDetailsError: state.uploadedDocuments && state.uploadedDocuments.error,
        userName: state.authentication?.data?.userName || "Unknown",
        customerId: state.authentication?.data?.customerId || [],
    };
}

function mapDispatchersToProps(dispatch) {
    return {
        onPageLoad: (apiParams) => {
            dispatch(DOCUMENTS_CONTROLLER.doLoadUploadedDocuments(apiParams));
        },
        doOperationOnDocument: (values, cb) => {
            dispatch(DOCUMENTS_CONTROLLER.doOperationOnDocument(values, cb));
        },
        doLoadDocumentDetails: (documentId) => {
            dispatch(DOCUMENTS_CONTROLLER.doLoadDocumentDetails(documentId));
        },
        doUnLoadDocumentDetails: () => {
            dispatch(DOCUMENTS_CONTROLLER.doUnLoadDocumentDetails());
        },
        doAddToast: (id, title, message, type) => {
            dispatch(toastActions.addToast(id, title, message, type, 'uploadedDocuments'));
        },
    };
}

export default connect(mapStateToProps, mapDispatchersToProps)(UploadedDocumentsPage);
